import {
  forwardRef,
  useReducer,
  useEffect,
  useCallback,
  LegacyRef,
} from 'react';
import classnames from 'classnames';

const initialState = {
  className: 'checkbox-active',
  disabled: false,
  checked: false,
};

type stateProps = {
  className: string;
  checked: boolean;
  disabled?: boolean;
};

type actionProps = {
  type: string;
  checked?: boolean;
  disabled?: boolean;
};

type checkboxTypeProps = {
  checked: boolean;
  disabled: boolean;
  name: string;
  onChange: (event: {
    target: {
      name: string;
      value: string;
      checked: boolean;
    };
  }) => void;
  value: string;
  isControlled: boolean;
  id: string;
};

const checkboxReducer = (state: stateProps, action: actionProps) => {
  switch (action.type) {
    case 'change': {
      if (action.checked) {
        return {
          ...state,
          className: 'checkbox-selected',
          checked: true,
        };
      }
      return {
        ...state,
        className: 'checkbox-active',
        checked: false,
      };
    }
    case 'disabled': {
      return {
        ...state,
        className: 'checkbox-disabled',
        disabled: action.disabled,
      };
    }
    default: {
      throw new Error(`Unhandled action: ${action.type}`);
    }
  }
};

export default forwardRef(function Checkbox(
  props: checkboxTypeProps,
  ref: LegacyRef<HTMLInputElement>,
) {
  const {
    checked,
    disabled,
    name,
    onChange,
    value,
    id,
    isControlled = false,
    ...inputProps
  } = props;

  const [state, dispatch] = useReducer(checkboxReducer, {
    ...initialState,
    checked,
  });

  useEffect(() => {
    if (typeof checked === 'boolean') {
      dispatch({ type: 'change', checked });
    }
  }, [checked]);

  useEffect(() => {
    if (disabled) {
      dispatch({ type: 'disabled', disabled });
    }
  }, [disabled, state.disabled]);

  const handleOnChange = useCallback(() => {
    onChange?.({ target: { name, value, checked: !state.checked } });

    if (!isControlled) dispatch({ type: 'change', checked: !state.checked });
  }, [name, onChange, isControlled, state.checked, value]);

  return (
    <div
      className={classnames('checkbox', state.className)}
      tabIndex={0}
      role="checkbox"
      aria-checked={state.checked}
      data-testid="checkbox-input"
    >
      <input
        id={id}
        type="checkbox"
        data-testid="checkbox"
        checked={state.checked}
        className="hidden"
        disabled={state.disabled}
        name={name}
        onChange={handleOnChange}
        ref={ref}
        value={value}
        {...inputProps}
      />
      <svg
        className="hidden"
        width="10"
        height="8"
        viewBox="0 0 10 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M3.75 7.4375L0.625 4.3375L1.61875 3.35625L3.75 5.46875L8.38125 0.875L9.375 1.8625L3.75 7.4375Z"
          fill="white"
        />
      </svg>
    </div>
  );
});
